/**
 * PendingProvidersCard Component (T115 - US5)
 * Displays pending providers in card list format
 * 
 * Note: Provider approval workflow is deferred to future implementation
 * This component displays pending providers for viewing/editing only
 */

import { Clock, Mail, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import type { AdminProvider } from '../types/admin-provider.type';
import { VettingCheckList } from '@/features/vetting/components/VettingCheckList';
import { useProviderVettingStatusBatch } from '@/features/vetting/hooks/useProviderVettingStatus';
import { useAdminAuth } from '../hooks/useAdminAuth';

type PendingProvidersCardProps = {
  providers: AdminProvider[];
  onView: (providerId: string) => void;
  isLoading?: boolean;
};

/**
 * Card list component for pending providers
 * 
 * @param providers - Array of pending providers
 * @param onView - Callback when view/edit button is clicked
 * @param isLoading - Whether data is loading
 * 
 * Features:
 * - Card-based layout for pending providers
 * - Displays company name, contact email, applied date
 * - Progress indicator (placeholder for future workflow)
 * - Status badge
 * - Empty state message
 */
export function PendingProvidersCard({
  providers,
  onView,
  isLoading = false,
}: PendingProvidersCardProps) {
  // One vetting-status request for every card on screen (not one per card, no polling)
  const vetting = useProviderVettingStatusBatch(providers.map((p) => p.id));
  const { canManageUsersAndResources } = useAdminAuth();

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
          >
            <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-64 bg-gray-100 rounded animate-pulse" />
            <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (providers.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
        <div className="text-gray-400 mb-2">
          <Clock className="h-12 w-12 mx-auto" />
        </div>
        <p className="text-gray-500 font-medium">No pending providers</p>
        <p className="text-gray-400 text-sm mt-1">
          All providers have been reviewed
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vetting.isError && (
        <div
          role="alert"
          className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <span>Could not load background check status.</span>
          <button type="button" onClick={vetting.refetch} className="font-medium underline">
            Retry
          </button>
        </div>
      )}
      {providers.map((provider) => {
        const displayName = provider.companyName || 
          `${provider.user.firstName} ${provider.user.lastName || ''}`.trim();
        
        const appliedDate = formatDistanceToNow(new Date(provider.createdAt), {
          addSuffix: true,
        });

        return (
          <div
            key={provider.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#026857] hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between">
              {/* Provider Info */}
              <div className="flex-1 space-y-2">
                {/* Company Name */}
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900">
                    {displayName}
                  </h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                </div>

                {/* Contact Email */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>{provider.user.email}</span>
                </div>

                {/* Applied Date */}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>Applied {appliedDate}</span>
                  <span className="text-gray-400">
                    ({format(new Date(provider.createdAt), 'MMM d, yyyy')})
                  </span>
                </div>

                {/* Location (if available) */}
                {provider.businessAddress && (
                  <div className="text-sm text-gray-600 mt-1">
                    📍 {provider.businessAddress}
                  </div>
                )}

                {/* Vetting check status (Feature 099 - Checkr integration) */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <VettingCheckList
                    providerId={provider.id}
                    compact
                    canManage={canManageUsersAndResources}
                    // null after a failed batch: render nothing instead of an endless skeleton
                    summary={vetting.isError ? null : vetting.summaries[provider.id]}
                    fromBatch
                  />
                </div>
              </div>

              {/* Action Button */}
              <button
                type="button"
                onClick={() => onView(provider.id)}
                className="ml-4 px-4 py-2 text-sm font-medium text-[#026857] bg-[#026857]/10 hover:bg-[#026857]/20 rounded-md transition-colors"
              >
                View Details
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
